import { SHStack, SHTypography } from "@components/design-systems";
import { RenderAnalysisSummaryItemProps } from "@models/platform-analysis/entities/steps/summary";
import { FeeCalculatorPortfolioLevel } from "@models/platform-analysis/enums/fee/portfolioLevel";
import { AnalysisStep } from "@models/platform-analysis/enums/step";
import { RootState } from "@redux/store";
import { memo, useMemo } from "react";
import { useSelector } from "react-redux";

export const SummaryWhatsIncluded = memo(
  ({ disabled: sectionDisabled }: RenderAnalysisSummaryItemProps) => {
    const analysisSteps = useSelector(
      (state: RootState) => state.platformAnalysis.analysisSteps,
    );

    const selectedProducts = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.selectedProducts,
    );

    const featureAnalysis = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.featureAnalysis,
    );

    const businessMetricAnalysis = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary
          ?.businessMetricAnalysis,
    );

    const feeAnalysis = useSelector(
      (state: RootState) =>
        state.platformAnalysis.platformAnalysis?.summary?.feeAnalysis,
    );

    const steps = analysisSteps?.steps ?? [];
    const hasBusinessMetric = steps.includes(AnalysisStep.BusinessMetric);
    const hasFee = steps.includes(AnalysisStep.Fee);

    const summaryData = useMemo(() => {
      return {
        totalPlatform: selectedProducts?.length,
        feature: {
          total: featureAnalysis?.all?.overallScore?.totalFeature,
          selected: featureAnalysis?.all?.overallScore?.totalSelectedFeature,
        },
        businessMetric: {
          total: businessMetricAnalysis?.all?.overallScore?.totalFeature,
          selected:
            businessMetricAnalysis?.all?.overallScore?.totalSelectedFeature,
        },
        fee: {
          portfolioSetup: feeAnalysis?.all?.portfolioSetup,
        },
      };
    }, [
      selectedProducts,
      featureAnalysis,
      businessMetricAnalysis,
      feeAnalysis,
    ]);

    const description = useMemo(() => {
      const parts: string[] = [];

      parts.push(
        `This Suitability Review assesses ${summaryData.totalPlatform} ${
          (summaryData.totalPlatform ?? 0) === 1 ? "platform" : "platforms"
        }`,
      );

      parts.push(
        `based on ${summaryData.feature.selected} of ${summaryData.feature.total} features`,
      );

      if (hasBusinessMetric) {
        parts.push(
          `${summaryData.businessMetric.selected} of ${summaryData.businessMetric.total} business metrics`,
        );
      }

      if (hasFee) {
        parts.push(
          `and platform ${
            summaryData.fee.portfolioSetup ===
            FeeCalculatorPortfolioLevel.Family
              ? "and managed account "
              : " "
          }fees`,
        );
      }

      let sentence = parts.join(", ") + ".";

      const excluded: string[] = [];
      if (!hasBusinessMetric) excluded.push("business metrics");
      if (!hasFee) excluded.push("platform fees");

      if (excluded.length) {
        sentence += ` The ${excluded.join(" and ")} section${
          excluded.length > 1 ? "s are" : " is"
        } excluded from this review.`;
      }

      return sentence;
    }, [hasBusinessMetric, hasFee, summaryData]);

    return (
      <SHStack width="100%">
        <SHTypography disabled={sectionDisabled} variant="subtitle2">
          What’s included in this Suitability Review
        </SHTypography>
        <SHTypography disabled={sectionDisabled} variant="body2">
          {description}
        </SHTypography>
      </SHStack>
    );
  },
);
